
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { UseFormWatch } from "react-hook-form";
import type { FormValues } from "../schema/assessmentSchema";

export const useAssessmentQueries = (
  watch: UseFormWatch<FormValues>,
  id: string | undefined,
  form: any
) => {
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [currentStudentId, setCurrentStudentId] = useState<string | null>(null);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'student_id') {
        setCurrentStudentId(value.student_id as string);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const { data: students } = useQuery({
    queryKey: ["students"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("status", "active")
        .order("first_name", { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const { data: previousAssessment } = useQuery({
    queryKey: ["previous-assessment", currentStudentId],
    queryFn: async () => {
      if (!currentStudentId) return null;

      const { data, error } = await supabase
        .from("physical_assessments")
        .select("*")
        .eq("student_id", currentStudentId)
        .order("assessment_date", { ascending: false })
        .limit(1);

      if (error) throw error;
      return data[0];
    },
    enabled: !!currentStudentId,
  });

  useQuery({
    queryKey: ["assessment", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("physical_assessments")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      if (data) {
        form.reset({
          ...data,
          assessment_date: new Date(data.assessment_date),
          weight: data.weight?.toString(),
          body_fat_percentage: data.body_fat_percentage?.toString(),
          muscle_mass: data.muscle_mass?.toString(),
          chest_measurement: data.chest_measurement?.toString(),
          waist_measurement: data.waist_measurement?.toString(),
          hip_measurement: data.hip_measurement?.toString(),
          arm_measurement: data.arm_measurement?.toString(),
          thigh_measurement: data.thigh_measurement?.toString(),
          resting_heart_rate: data.resting_heart_rate?.toString(),
        });
        setCurrentStudentId(data.student_id);
      }

      return data;
    },
    enabled: !!id,
  });

  useQuery({
    queryKey: ["student-details", currentStudentId],
    queryFn: async () => {
      if (!currentStudentId) return null;

      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("id", currentStudentId)
        .single();

      if (error) throw error;
      setSelectedStudent(data);
      return data;
    },
    enabled: !!currentStudentId,
  });

  return {
    students,
    previousAssessment,
    selectedStudent,
    currentStudentId,
  };
};
